import { useBrand, BRANDS } from '@cof/plastic-components';
import { css, styled } from 'styled-components';
import Header from '../../components/Header';
import Main from '../../components/Main';
import CreditWiseSection from '../../components/pages/ScoreDecline/CreditWiseSection';
import GetToKnow from '../../components/pages/ScoreDecline/GetToKnow';
import TitleSection, { TitleSectionHeading, TitleSectionSubText } from '../../components/TitleSection';
import useBackground from '../../utilities/useBackground';
import PropTypes from 'prop-types';
import { useAggregatorConsumerName } from '../../utilities/aggregatorConsumerNameProvider';
import { AGGREGATOR_WITH_LOGO } from '../../utilities/constants';
import { useQualtrics } from '../../utilities/useQualtrics';
import { useQualtricsScrollTrigger } from '../../utilities/useQualtricsScrollTrigger';

const StyledTitleSection = styled(TitleSection)`
    ${({ theme }: any) => css`
        ${theme.components.TitleSectionNoPadding}
    `}
`;

const getPaddingBottomDesktop = (theme: any, $requoteOptIn: any, $brand: any) => {
    return $requoteOptIn === 'true'
        ? $brand === BRANDS.CAPITAL_ONE
            ? theme.space.lg
            : theme.space.sm
        : theme.space.xl;
};

const StyledTitleSectionHeading = styled(TitleSectionHeading)`
    ${({ theme, $brand, $requoteOptIn, $aggregatorConsumerName }: any) => css`
        padding-top: ${AGGREGATOR_WITH_LOGO.includes($aggregatorConsumerName) ? theme.space.lg : 0};
        padding-bottom: ${$requoteOptIn === 'true' ? theme.space.sm : theme.space.lg};
        margin: ${$brand === BRANDS.CAPITAL_ONE
            ? `0 ${theme.space.lg} 0 ${theme.space.lg}`
            : `0 ${theme.space.sm} 0 ${theme.space.sm}`};
        ${theme.components.ScoreDeclineHeaderColour}

        ${theme.breakpoints.mq.tabletMin} {
            width: 90%;
            padding-top: ${AGGREGATOR_WITH_LOGO.includes($aggregatorConsumerName) ? theme.space.xl : 0};
            padding-bottom: ${getPaddingBottomDesktop(theme, $requoteOptIn, $brand)};
            max-width: 90rem;
            margin: 0 auto;
        }
    `}
`;

const StyledTitleSectionSubText = styled(TitleSectionSubText)`
    ${({ theme }: any) => css`
        padding-bottom: ${theme.space.lg};
        margin: 0 ${theme.space.lg} 0 ${theme.space.lg};
        text-align: center;
        ${theme.components.ScoreDeclineTextColour}

        ${theme.breakpoints.mq.tabletMin} {
            width: 90%;
            padding-bottom: ${theme.space.xl};
            max-width: 90rem;
            margin: 0 auto;
        }
    `}
`;

export const ScoreDeclineBody = () => {
    const brand = useBrand();

    if (brand === BRANDS.CAPITAL_ONE) {
        return <CreditWiseSection />;
    }

    return <GetToKnow />;
};

const ScoreDecline = ({ firstName, requoteOptIn, isObDeclineConsent }: any) => {
    const background = useBackground('scoreDecline');
    const brand = useBrand();
    const [aggregatorConsumerName] = useAggregatorConsumerName();

    const qualtricsEnabled = isObDeclineConsent && brand == 'capitalOne' && !aggregatorConsumerName;
    useQualtrics(qualtricsEnabled);
    useQualtricsScrollTrigger(qualtricsEnabled);

    return (
        <>
            <Header isDeclinePage={true} />
            <Main bg={background} data-qa-id="score-decline-page">
                <StyledTitleSection>
                    <StyledTitleSectionHeading
                        $requoteOptIn={requoteOptIn}
                        $brand={brand}
                        $aggregatorConsumerName={aggregatorConsumerName}
                        data-qa-id="page-title"
                    >
                        {`Sorry${firstName ? ` ${firstName}` : ''}, `}
                        <span data-qm-allow="true">we couldn&apos;t give you a card this time</span>
                    </StyledTitleSectionHeading>
                    {requoteOptIn === 'true' && (
                        <StyledTitleSectionSubText data-qa-id="subtitle" data-qm-allow>
                            Don’t worry though. We’ll let you know when you’re eligible in future, without affecting
                            your credit score.
                        </StyledTitleSectionSubText>
                    )}
                </StyledTitleSection>
                <>
                    <ScoreDeclineBody />
                </>
            </Main>
        </>
    );
};

ScoreDecline.propTypes = {
    requoteOptIn: PropTypes.string,
    firstName: PropTypes.string,
    isObDeclineConsent: PropTypes.bool
};

export default ScoreDecline;
